<template>
  <ViewContainer
    :title="$t('boards.board_other')"
    :style="{ 'max-width': $vuetify.breakpoint.xlOnly ? '1785px' : 'none' }"
  >
    <template v-slot:header-right>
      <CreateButton
        small
        tourId="boards-create-button"
        :tooltipText="$t('boards.create_board')"
        @click="handleGoToCreateBoard"
      />
    </template>
    <v-card elevation="2" class="px-6 pt-4 pb-6">
      <div class="d-flex align-center justify-space-between">
        <div class="d-flex">
          <!-- TODO: add this button variable to DS -->
          <v-btn
            class="me-2"
            rounded
            small
            elevation="0"
            :outlined="selectedFilter !== FILTERS.ALL_BOARDS"
            :color="
              selectedFilter === FILTERS.ALL_BOARDS
                ? 'primary'
                : 'blue-grey lighten-2'
            "
            @click="selectFilter(FILTERS.ALL_BOARDS)"
          >
            <span
              :class="
                selectedFilter === FILTERS.ALL_BOARDS
                  ? 'white--text'
                  : 'blue-grey--text text--darken-2'
              "
            >
              {{ $t('boards.all_boards') }}
            </span>
          </v-btn>
          <v-btn
            class="me-2"
            rounded
            small
            elevation="0"
            :outlined="selectedFilter !== FILTERS.MY_BOARDS"
            :color="
              selectedFilter === FILTERS.MY_BOARDS
                ? 'primary'
                : 'blue-grey lighten-2'
            "
            @click="selectFilter(FILTERS.MY_BOARDS)"
          >
            <span
              :class="
                selectedFilter === FILTERS.MY_BOARDS
                  ? 'white--text'
                  : 'blue-grey--text text--darken-2'
              "
            >
              {{ $t('boards.my_boards') }}
            </span>
          </v-btn>
          <v-btn
            rounded
            class="me-2"
            small
            elevation="0"
            :outlined="selectedFilter !== FILTERS.SHARED_BOARDS"
            :color="
              selectedFilter === FILTERS.SHARED_BOARDS
                ? 'primary'
                : 'blue-grey lighten-2'
            "
            @click="selectFilter(FILTERS.SHARED_BOARDS)"
          >
            <span
              :class="
                selectedFilter === FILTERS.SHARED_BOARDS
                  ? 'white--text'
                  : 'blue-grey--text text--darken-2'
              "
            >
              {{ $t('boards.shared_with_me') }}
            </span>
          </v-btn>
        </div>
        <SearchInput
          tourId="boards-search-input"
          style="max-width: 300px;"
          :value="terms"
          @change="searchBoards"
        />
      </div>
      <div v-if="loading" class="mt-6">
        <v-row class="ma-0 pa-0 mx-n2">
          <v-col
            v-for="index in totalItemsPerPage"
            sm="12"
            md="6"
            lg="4"
            class="mt-0 mb-4 px-2"
            :key="index"
          >
            <SkeletonCard />
          </v-col>
        </v-row>
      </div>
      <div v-if="!showEmptyState && !showSearchEmptyState" class="mt-6">
        <v-row class="ma-0 pa-0 mx-n2">
          <v-col
            v-for="(board, index) in boardsToShow"
            sm="12"
            md="6"
            lg="4"
            class="mt-0 mb-4 px-2"
            :key="`${board.id}${index}`"
          >
            <Card
              :loading="deleting && board.id === currentBoard?.id"
              :board="board"
              @pin="handlePinBoard(board)"
              @delete="handleDeleteBoardClick(board)"
              @duplicate="duplicateBoard(board)"
              @share="openShareModal(board)"
              @unfollow="openUnfollowModal(board)"
              @copy="handleCopyLink(board)"
            />
          </v-col>
        </v-row>
        <v-pagination
          v-if="shouldDisplayPagination"
          circle
          class="mt-4"
          total-visible="10"
          data-testid="boards-pagination"
          :length="pages"
          :value="currentPage"
          @input="changePage"
        />
      </div>
      <EmptyState
        v-if="showEmptyState"
        :shared="selectedFilter === FILTERS.SHARED_BOARDS"
        @createBoard="handleGoToCreateBoard"
      />
      <SearchEmptyState v-if="showSearchEmptyState" />
    </v-card>
    <DeleteConfirmationModal
      :title="$t('boards.delete_title')"
      :text="$t('boards.delete_subtitle', { name: currentBoard?.name })"
      :show="showDeleteConfirmationDialog"
      :isLoading="deleting"
      @cancel="cancelDeleteBoard"
      @delete="deleteBoard"
    />
    <ShareBoardModal
      v-if="showShareModal"
      :boardId="currentBoard.id"
      @close="showShareModal = false"
      @save="showShareModal = false"
    />
    <DeleteConfirmationModal
      :show="showUnfollowModal"
      :title="$t('boards.unfollow_board')"
      :text="$t('boards.unfollow_board_message', { name: currentBoard?.name })"
      :confirmText="$t('common.unfollow')"
      :isLoading="deleting"
      @cancel="handleCancelUnfollow"
      @delete="handleUnfollowBoard"
    />
    <TemplatesModal
      :show="showTemplatesModal"
      @close="showTemplatesModal = false"
    />
  </ViewContainer>
</template>

<script>
import _ from 'lodash'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import CreateButton from '@/components/UI/CreateButton.vue'
import SearchInput from '@/components/Core/SearchInput.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import ShareBoardModal from '@/components/Modals/ShareBoardModal.vue'

import Card from './Card.vue'
import SkeletonCard from './SkeletonCard.vue'
import EmptyState from './EmptyState.vue'
import SearchEmptyState from './SearchEmptyState.vue'
import TemplatesModal from './TemplatesModal.vue'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import { mapGetters } from 'vuex'

const FILTERS = {
  ALL_BOARDS: 'all_boards',
  MY_BOARDS: 'my_boards',
  SHARED_BOARDS: 'shared_to_me',
}

export default {
  name: 'Boards',

  mixins: [mixin, trackerMixin],

  components: {
    ViewContainer,
    CreateButton,
    SearchInput,
    DeleteConfirmationModal,
    ShareBoardModal,
    Card,
    EmptyState,
    SkeletonCard,
    SearchEmptyState,
    TemplatesModal,
  },

  data() {
    return {
      FILTERS,
      selectedFilter: FILTERS.ALL_BOARDS,
      deleting: false,
      showDeleteConfirmationDialog: false,
      showShareModal: false,
      showTemplatesModal: false,
      showUnfollowModal: false,
      boards: [],
      pinnedBoards: [],
      loading: true,
      terms: '',
      totalBoards: 0,
      currentPage: 1,
      currentBoard: null,
      totalPinnedBoards: 0,
      totalItemsPerPage: 21,
    }
  },

  async mounted() {
    if (!this.organization.featuresEnabled__boards) {
      return this.$router.push({ name: 'error404' })
    }

    await this.getBoards()
  },

  computed: {
    ...mapGetters(['organizationId', 'accountId', 'organization']),

    boardsToShow() {
      return [...this.pinnedBoards, ...this.boards]
    },

    nonPinnedBoardsPerPage() {
      return this.totalItemsPerPage - this.totalPinnedBoards
    },

    showEmptyState() {
      return !this.loading && !this.terms && !this.boardsToShow.length
    },

    showSearchEmptyState() {
      return Boolean(!this.loading && this.terms && !this.boardsToShow.length)
    },

    shouldDisplayPagination() {
      return this.pages > 1
    },

    pages() {
      const pinnedAndUnpinnedBoards = this.totalBoards + this.totalPinnedBoards

      return Math.ceil(pinnedAndUnpinnedBoards / this.totalItemsPerPage)
    },
  },

  methods: {
    handleCancelUnfollow() {
      this.trackBoardsActions('boards.CancelAction', {
        action: 'Unfollow modal',
      })
      this.showUnfollowModal = false
    },

    async selectFilter(filter) {
      this.selectedFilter = filter
      this.currentPage = 1
      this.terms = ''
      this.trackBoardsActions('boards.SelectFilter', { selectedFilter: filter })
      await this.getBoards()
    },

    async handlePinBoard(board) {
      if (this.totalPinnedBoards === 3 && !board.isPinnedByMe)
        return this.showError(this.$t('boards.pinned_boards_error'))
      try {
        this.currentBoard = { ...board }
        this.loading = true
        if (board.isPinnedByMe) {
          await api.board.unPin(this.organizationId, board.id)
        } else {
          await api.board.pin(this.organizationId, board.id)
        }

        // the read action have more priority than write actions in stream, this helps to prevent wrong get response after the pin/unpin action
        _.delay(async () => await this.getBoards(), 500)

        this.trackBoardsActions(
          board.isPinnedByMe ? 'boards.UnpinBoard' : 'boards.PinBoard'
        )

        this.showSuccess(
          board.isPinnedByMe
            ? this.$t('boards.board_unpinned_successfully')
            : this.$t('boards.board_pinned_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.currentBoard = null
      }
    },

    async getBoards() {
      await this.getPinnedBoards()
      await this.getAllBoards()
    },

    getBoardName(originalName) {
      const maxLength = 60
      const copy = ` ${this.$t('boards.copy')}`

      const availableLength = maxLength - copy.length

      const trimmedName = originalName.substring(0, availableLength)

      return trimmedName + copy
    },

    async duplicateBoard(originalBoard) {
      try {
        this.currentBoard = originalBoard

        const board = {
          name: this.getBoardName(originalBoard.name),
          description: originalBoard?.description ?? '',
        }

        const { id } = await this.createBoard(board)

        const originalColumns = await this.getColumns(originalBoard.id)

        const columns = originalColumns.map(col => {
          return {
            name: col.name,
            sort: col?.sort ?? { by: 'createdAt', order: 'asc' },
            filter: col.filter,
            position: col.position,
          }
        })

        await this.createColumns(id, columns)
        await this.getAllBoards()
        this.trackBoardsActions('boards.DuplicateBoard')
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.currentBoard = null
      }
    },

    async createBoard(board) {
      const response = await api.board.create({
        organizationId: this.organizationId,
        data: board,
      })
      return response.data
    },

    async getColumns(boardId) {
      const response = await api.board.getColumns({
        organizationId: this.organizationId,
        boardId,
      })
      return response.data.results
    },

    async createColumns(boardId, columns) {
      const response = await api.board.createColumnBulk({
        organizationId: this.organizationId,
        boardId,
        columns: columns,
      })
      return response.data
    },

    async deleteBoard() {
      try {
        this.deleting = true
        await api.board.delete(this.organizationId, this.currentBoard?.id)

        if (this.boardsToShow.length - 1 === this.totalPinnedBoards)
          this.currentPage = 1

        // the read action have more priority than write actions in stream, this helps to prevent wrong get response after the delete action
        _.delay(async () => await this.getBoards(), 500)

        this.showDeleteConfirmationDialog = false
        this.trackBoardsActions('boards.ConfirmAction', {
          action: 'delete board',
        })
        this.showSuccess(this.$t('boards.board_deleted_successfully'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.deleting = false
      }
    },

    handleDeleteBoardClick(board) {
      this.currentBoard = board
      this.showDeleteConfirmationDialog = true
      this.trackBoardsActions('boards.DeleteBoard')
    },

    async searchBoards(terms) {
      this.terms = terms
      this.currentPage = 1
      await this.getBoards()
    },

    async changePage(page) {
      this.currentPage = page

      await this.getAllBoards()
    },

    getFilters() {
      switch (this.selectedFilter) {
        case FILTERS.ALL_BOARDS:
          return {
            readAccounts: [this.accountId],
            ownerAccounts: [this.accountId],
          }
        case FILTERS.MY_BOARDS:
          return {
            ownerAccounts: [this.accountId],
          }
        case FILTERS.SHARED_BOARDS:
          return {
            readAccounts: [this.accountId],
          }
      }
    },

    async getAllBoards() {
      try {
        this.loading = true
        const response = await api.board.getStreamBoards(this.organizationId, {
          sort: { by: this.terms ? '_score' : 'name', order: 'desc' },
          limit: this.nonPinnedBoardsPerPage,
          from: this.nonPinnedBoardsPerPage * (this.currentPage - 1),
          terms: this.terms,

          expansions: ['createdBy'],
          filters: { excludePin: [this.accountId], ...this.getFilters() },
        })

        this.totalBoards = response.data.totalRecordsInData
        this.boards = response.data.results.map(board => {
          return { ...board, isPinnedByMe: false }
        })
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    async getPinnedBoards() {
      try {
        this.loading = true
        const response = await api.board.getStreamBoards(this.organizationId, {
          sort: { by: this.terms ? '_score' : 'name', order: 'desc' },
          limit: 3,
          from: this.nonPinnedBoardsPerPage * (this.currentPage - 1),
          terms: this.terms,
          expansions: ['createdBy'],
          filters: { pin: [this.accountId], ...this.getFilters() },
        })

        this.pinnedBoards = response.data.results.map(board => {
          return { ...board, isPinnedByMe: true }
        })

        this.totalPinnedBoards = response.data.totalRecordsInData
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.loading = false
      }
    },

    cancelDeleteBoard() {
      this.showDeleteConfirmationDialog = false
      this.trackBoardsActions('boards.CancelAction', {
        action: 'delete board',
      })
    },

    openShareModal(board) {
      this.trackBoardsActions('boards.ShareAction', {
        section: 'all boards',
        action: 'open share board modal',
      })
      this.currentBoard = board
      this.showShareModal = true
    },

    openUnfollowModal(board) {
      this.trackBoardsActions('boards.UnfollowBoard', {
        action: 'Open unfollow modal',
      })
      this.currentBoard = board
      this.showUnfollowModal = true
    },

    handleGoToCreateBoard() {
      this.showTemplatesModal = true
    },

    async handleUnfollowBoard() {
      try {
        this.trackBoardsActions('boards.ConfirmAction', {
          action: 'Unfollow modal',
        })
        this.deleting = true
        await api.board.unfollow(
          this.organizationId,
          this.currentBoard?.id,
          this.accountId
        )

        await this.getBoards()

        this.showUnfollowModal = false
        this.showSuccess(this.$t('boards.board_successfully_unfollowed'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.deleting = false
      }
    },

    handleCopyLink(board) {
      this.trackBoardsActions('boards.CopyLink', {
        section: 'boardCard',
      })
      const boardLink = `${window.location.origin}/boards/${board.id}`
      navigator.clipboard.writeText(boardLink)
      this.showSuccess(this.$t('common.link_copy_to_clipboard'))
    },
  },
}
</script>
